import React, { useEffect, useRef } from "react";
import clsx from "clsx";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import {
  setImported,
  setProject,
  setVehicles,
  setVehicleTypes
} from "../../store/actions/project";
import { setLoadingImport } from "../../store/actions/loading";
import API from "../../utils/axios";
import Box from "@material-ui/core/Box";
import Typography from "@material-ui/core/Typography";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";

import XlsxExampleFile from "../data/import/sample-vehicles.xlsx";
import DataTable from "./DataTable";
import DragAndDrop from "./DragAndDrop";
import ExampleTable from "./ExampleTable";
import BackIcon from "@material-ui/icons/ArrowBack";
import xlsxIcon from "../../assets/images/icons/xlsx-icon.svg";
import Fade from "@material-ui/core/Fade";
import LoadingCircle from "../common/LoadingCircle";
import { vehiclesExample } from "../data/import/examples";
import { Trans } from "react-i18next";
import ExpansionPanel from "@material-ui/core/ExpansionPanel";
import ExpansionPanelSummary from "@material-ui/core/ExpansionPanelSummary";
import ExpansionPanelDetails from "@material-ui/core/ExpansionPanelDetails";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Chip from "@material-ui/core/Chip";
import BlackButton from "../accountDrawer/BlackButton";
import { useSnackbar } from "notistack";
import { defaultCapacity } from "../data/constants";
import { useImportStyles } from "./commonStyles";

function ImportVehicles({
  history,
  setImported,
  setLoadingImportLeft = () => {},
  loadingImport,
  validVehicles,
  invalidVehicles,
  setUploadOpen,
  setVehicleTypes,
  vehicleTypes
}) {
  const classes = useImportStyles();

  const importBody = useRef();

  const { enqueueSnackbar } = useSnackbar();

  const handleFetch = ({ body, blank }) => {
    if (blank) {
      enqueueSnackbar("The uploaded file is blank.");
      setLoadingImportLeft(false);
      setLoadingImport(false);
      return;
    }

    let vehicles = body.filter(
      obj => Object.keys(obj).filter(el => obj[el]).length
    );

    vehicles = vehicles.map((el, i) => ({
      name: el.license_plate,
      quantity: el.max_units,
      volume: el.max_volume,
      weight: el.max_weight,
      tags: el.vehicle_tags?.split(";").map(item => item.trim()) || null,
      profile: "car"
    }));

    setLoadingImportLeft(true);
    API.post(`/v1/vehicles/validation`, vehicles)
      .then(res => {
        console.log(res);
        res.data.valid = res.data.valid.map(item => {
          const { name, quantity, volume, weight, tags } = item;
          return {
            license_plate: name,
            max_units: quantity,
            max_volume: volume,
            max_weight: weight,
            vehicle_tags: tags?.join("; ")
          };
        });
        res.data.invalid = res.data.invalid.map(item => {
          const { name, quantity, volume, weight, tags } = item;
          return {
            license_plate: name,
            max_units: quantity,
            max_volume: volume,
            max_weight: weight,
            vehicle_tags: tags?.join("; ")
          };
        });
        setImported(res.data.valid, res.data.invalid, "vehicles");
        importBody.current.scrollTop = importBody.current.scrollHeight;
      })
      .catch(error => {
        console.log(error);
        if (error && error.status === 400) {
          setImported([], [...validVehicles, ...invalidVehicles], "vehicles");
        } else {
          enqueueSnackbar("Something went wrong");
        }
      })
      .finally(() => {
        setLoadingImportLeft(false);
        setLoadingImport(false);
      });
  };

  const handleUpload = () => {
    setLoadingImportLeft(true);
    setLoadingImport(true);
    if (validVehicles.length) {
      API.post(
        `/v1/vehicles/batch`,
        validVehicles.map(vehicle => ({
          name: vehicle.license_plate,
          quantity: vehicle.max_units || defaultCapacity,
          weight: vehicle.max_weight || null,
          volume: vehicle.max_volume || null,
          tags:
            vehicle.vehicle_tags?.split(";").map(item => item.trim()) || null,
          profile: "car"
        }))
      )
        .then(res => {
          console.log(res);
          // setDrivers(res.data);

          setImported(validVehicles, invalidVehicles, "vehicles");
          // setVehicles;
          // setVehicleTypes;
          handleUpdate(res.data);
        })
        .catch(error => {
          console.log(error);
          if (error && error.status === 400) {
            setImported([], [...validVehicles, ...invalidVehicles], "vehicles");
          } else {
            enqueueSnackbar("Something went wrong");
          }
        })
        .finally(() => {
          setLoadingImportLeft(false);
          setLoadingImport(false);
        });
    } else {
      setImported(validVehicles, invalidVehicles, "vehicles");
      setLoadingImportLeft(false);
      setLoadingImport(false);
    }

    const handleUpdate = newVehicleTypes => {
      setVehicleTypes([...vehicleTypes, ...newVehicleTypes]);

      setLoadingImportLeft(false);
      setLoadingImport(false);
      if (setUploadOpen) {
        setUploadOpen(false);
      } else {
        history.replace("/vehicles");
      }
    };
  };

  useEffect(() => {
    return () => setImported([], []);
  }, [setImported]);

  const fields = [
    { name: "license_plate", required: true },
    { name: "max_units" },
    { name: "max_weight" },
    { name: "max_volume" },
    { name: "vehicle_tags" }
  ];

  return (
    <div className={classes.root}>
      {/* Loading */}
      <Fade in={loadingImport} mountOnEnter unmountOnExit>
        <LoadingCircle />
      </Fade>

      {/* Header */}
      <Box className={classes.header} display="flex" alignItems="center" px={3}>
        <IconButton
          // component={RouterLink}
          onClick={e => {
            if (setUploadOpen) {
              e.preventDefault();
              setUploadOpen(false);
            } else {
              history.replace("/vehicles");
            }
          }}
          data-cy="vehicles-closeUpload"
          className={classes.backButton}
        >
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            height="100%"
          >
            <BackIcon />
          </Box>
        </IconButton>

        <Typography variant="h5" className={classes.title}>
          <Trans i18nKey="uploadVehicles">Upload vehicles</Trans>
        </Typography>
      </Box>

      <Box className={classes.body} ref={importBody} pt={4} pb={5}>
        {validVehicles.length + invalidVehicles.length > 0 ? (
          <Box px={3}>
            {validVehicles.length ? (
              <Box
                style={{
                  overflow: "auto",
                  backgroundColor: "rgb(250, 250, 250)",
                  borderRadius: 4,
                  border: "1px dashed rgb(238, 238, 238)"
                }}
                py={1.5}
                px={1}
                mb={2}
              >
                <Typography>Valid vehicles</Typography>
                <DataTable tableData={validVehicles} validRows />
              </Box>
            ) : null}

            {invalidVehicles.length ? (
              <>
                <Box
                  style={{
                    overflow: "auto",
                    backgroundColor: "rgb(250, 250, 250)",
                    borderRadius: 4,
                    border: "1px dashed rgb(238, 238, 238)"
                  }}
                  py={1.5}
                  px={1}
                  mb={2}
                >
                  <Typography>Invalid vehicles</Typography>
                  <DataTable tableData={invalidVehicles} invalidRows />
                </Box>

                <Typography variant="body1">
                  Please check/fix the required fields from guide and upload
                  vehicles again, otherwise invalid vehicles will be ignored
                  during&nbsp;optimization
                </Typography>
              </>
            ) : null}

            <Box
              display="flex"
              alignItems="center"
              justifyContent="flex-end"
              my={3}
            >
              <Button
                component="button"
                onClick={() => {
                  importBody.current.scrollTop = 0;

                  setImported([], []);
                }}
                style={{ marginRight: 8 }}
                className={classes.btn}
              >
                Cancel
              </Button>

              <BlackButton
                disabled={!validVehicles.length}
                handleClick={handleUpload}
                content="Done"
                id="uploadVehicles-submitButton"
              />
            </Box>
          </Box>
        ) : (
          <>
            <Box pb={2} px={3}>
              <Typography variant="body1" style={{ fontWeight: 500 }}>
                Upload multiple vehicles from a file
              </Typography>

              <Typography variant="body2" style={{ color: "#808080" }}>
                Supported file formats: .xls, .xlsx
              </Typography>
            </Box>

            {/* Body */}
            <DragAndDrop
              handleFetch={handleFetch}
              setLoadingImportLeft={setLoadingImportLeft}
              isLeftUpload={Boolean(setLoadingImportLeft)}
            />

            <Box mt={2} px={3}>
              <Box mb={3}>
                <Typography
                  color="secondary"
                  variant="body2"
                  style={{ fontWeight: 600 }}
                >
                  <Trans i18nKey="templateFiles">Template file</Trans>:
                </Typography>

                <Box display="flex" alignItems="center" mt={1}>
                  <Box
                    component="a"
                    display="flex"
                    alignItems="flex-end"
                    color={"#1329FE"}
                    data-cy="vehicleTemplate"
                    href={XlsxExampleFile}
                    download
                    style={{ textDecoration: "none" }}
                    mr={2}
                  >
                    <Box component="img" src={xlsxIcon} alt="XLSX" mr={0.5} />
                    .XLSX
                  </Box>

                  {/*<Box*/}
                  {/*  component="a"*/}
                  {/*  display="flex"*/}
                  {/*  alignItems="flex-end"*/}
                  {/*  color={blue.A400}*/}
                  {/*  href={ExampleFile}*/}
                  {/*  download*/}
                  {/*  style={{ textDecoration: "none" }}*/}
                  {/*>*/}
                  {/*  <Box component="img" src={csvIcon} alt="CSV" mr={0.5} />*/}
                  {/*  .CSV*/}
                  {/*</Box>*/}
                </Box>
              </Box>
            </Box>

            <Box pt={1} px={3} pb={4} mt={1.25} className={classes.section}>
              <ExpansionPanel defaultExpanded>
                <ExpansionPanelSummary
                  expandIcon={
                    <ExpandMoreIcon data-cy="vehicle-expandColumns" />
                  }
                  className={classes.panelSummary}
                  aria-controls="panel1a-content"
                  id="panel1a-header"
                >
                  <Box>
                    <Box component="p" fontWeight="fontWeightBold">
                      Columns
                    </Box>

                    <Typography variant="body2">
                      Columns should be in the correct order and fields marked
                      in * are required.
                    </Typography>
                  </Box>
                </ExpansionPanelSummary>

                <ExpansionPanelDetails>
                  <Box display="flex" flexWrap="wrap" pt={1}>
                    {fields.map((el, i) => (
                      <Chip
                        key={i}
                        label={`${el.name}${el.required ? "*" : ""}`}
                        className={clsx(classes.chip, {
                          [classes.chipDark]: el.required
                        })}
                      />
                    ))}
                  </Box>
                </ExpansionPanelDetails>
              </ExpansionPanel>
            </Box>

            <Box pt={1} pb={2} px={3} mt={1.25} className={classes.section}>
              <Box component="p" fontWeight="fontWeightBold">
                Example
              </Box>

              <Box
                mt={3}
                mb={1}
                p={1}
                style={{
                  overflow: "auto",
                  backgroundColor: "#F7F7F7",
                  borderRadius: 4,
                  border: "1px dashed #eee"
                }}
              >
                <ExampleTable tableData={vehiclesExample} />
              </Box>
            </Box>
          </>
        )}
      </Box>
    </div>
  );
}

function mapStateToProps(state, props) {
  return {
    imported: state.common.imported,
    validVehicles:
      state.project.importedData.type === "vehicles"
        ? state.project.importedData.valid
        : [],
    invalidVehicles:
      state.project.importedData.type === "vehicles"
        ? state.project.importedData.invalid
        : [],
    vehicles: state.project.vehicles,
    vehicleTypes: state.project.vehicleTypes,
    loadingImport:
      props.loadingImport === undefined
        ? state.loading.importPage
        : props.loadingImport
    // project: state.project
  };
}

export default connect(mapStateToProps, {
  setImported,
  setProject,
  setLoadingImport,
  setVehicles,
  setVehicleTypes
})(withRouter(ImportVehicles));
